class InfoTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.infoTabsInitialized === 'true') {
      return;
    }

    this.dataset.infoTabsInitialized = 'true';
    this.abortController = new AbortController();
    this.tabsContainer = this.querySelector('[data-tabs-container]');
    this.labels = Array.from(this.querySelectorAll('label[data-tab]'));
    this.inputs = this.labels.map((label) => label.querySelector('input[type="radio"]')).filter(Boolean);
    this.panels = Array.from(this.querySelectorAll('[data-content-entry]'));
    this.clinicalSwipers = [];

    this.inputs.forEach((input) => {
      input.addEventListener('change', () => this.select(input), { signal: this.abortController.signal });
    });

    this.labels.forEach((label) => {
      label.addEventListener('keydown', (event) => this.handleKeydown(event, label), { signal: this.abortController.signal });
    });

    this.tabsContainer?.addEventListener('scroll', () => this.updateScrollState(), { signal: this.abortController.signal, passive: true });

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-content-entry]');

        if (panel) {
          const input = this.getInputById(panel.dataset.id);
          this.select(input, false);
        }
      }, { signal: this.abortController.signal });
    }

    this.select(this.inputs.find((input) => input.checked) || this.inputs[0], false);
    this.initSwiper();
    this.initClinicalSwipers();
    this.updateFitState();
    this.updateScrollState();

    this.resizeObserver = new ResizeObserver(() => {
      this.updateFitState();
      this.updateScrollState();
    });
    this.resizeObserver.observe(this.tabsContainer ?? this);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.swiper?.destroy?.();
    this.clinicalSwipers?.forEach((swiper) => swiper.destroy?.());
  }

  select(input, focus = true) {
    if (!input) {
      return;
    }

    input.checked = true;
    const label = input.closest('label[data-tab]');
    const id = label?.dataset.id;

    this.labels.forEach((tabLabel) => {
      if (tabLabel === label) {
        tabLabel.setAttribute('data-active', '');
      } else {
        tabLabel.removeAttribute('data-active');
      }
    });

    this.panels.forEach((panel) => {
      if (panel.dataset.id === id) {
        panel.setAttribute('data-active', '');
      } else {
        panel.removeAttribute('data-active');
      }
    });

    if (focus) {
      label?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      input.focus({ preventScroll: true });
    }

    this.updateScrollState();
  }

  handleKeydown(event, label) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }

    event.preventDefault();

    const index = this.labels.indexOf(label);
    let nextIndex = index;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.labels.length - 1;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + this.labels.length) % this.labels.length;
    } else {
      nextIndex = (index + 1) % this.labels.length;
    }

    this.select(this.labels[nextIndex]?.querySelector('input[type="radio"]'));
  }

  getInputById(id) {
    return this.labels.find((label) => label.dataset.id === id)?.querySelector('input[type="radio"]');
  }

  initSwiper() {
    const swiperElement = this.querySelector('[data-tabs-swiper]');

    if (!swiperElement || !window.Swiper) {
      return;
    }

    this.swiper = new window.Swiper(swiperElement, {
      slidesPerView: 'auto',
      freeMode: true,
      watchOverflow: true,
      resistanceRatio: 0,
    });
  }

  initClinicalSwipers() {
    if (!window.Swiper) {
      return;
    }

    this.querySelectorAll('[data-clinical-swiper]').forEach((swiperElement) => {
      const pagination = swiperElement.closest('.clinical-evidence')?.querySelector('.clinical-evidence__pagination');

      this.clinicalSwipers.push(new window.Swiper(swiperElement, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchOverflow: true,
        pagination: pagination ? {
          el: pagination,
          clickable: true,
        } : undefined,
      }));
    });
  }

  updateFitState() {
    const element = this.tabsContainer;

    if (!element) {
      return;
    }

    const wrapper = element.querySelector('.swiper-wrapper');
    const contentWidth = wrapper ? wrapper.scrollWidth : element.scrollWidth;
    // Fit (center) when all tabs fit; otherwise allow horizontal scrolling.
    const fits = contentWidth <= element.clientWidth + 1;

    element.toggleAttribute('data-tabs-fit', fits);
  }

  updateScrollState() {
    const element = this.tabsContainer;

    if (!element) {
      return;
    }

    const isAtEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 2;

    if (isAtEnd) {
      element.setAttribute('data-tabs-end', '');
    } else {
      element.removeAttribute('data-tabs-end');
    }
  }
}

if (!window.customElements.get('info-tabs')) {
  window.customElements.define('info-tabs', InfoTabs);
}
